import { c as _c } from "react/compiler-runtime";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { ChatRestriction, useDropPrivileges } from "@/hooks/useDropPriviledges";
import { useWaveEligibility } from "@/contexts/wave/WaveEligibilityContext";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useAuth } from "../auth/Auth";
import DropPlaceholder from "./DropPlaceholder";
import CreateDrop from "./CreateDrop";
import { DropMode, type CurationComposerVariant, type IdentityPickerPlacement } from "./dropComposer.types";
export { DropMode } from "./dropComposer.types";
interface PrivilegedDropCreatorProps {
  readonly activeDrop: ActiveDropState | null;
  readonly onCancelReplyQuote: () => void;
  readonly onDropAddedToQueue: () => void;
  readonly onAllDropsAdded?: (() => void) | undefined;
  readonly onServerDropCreated?: ((drop: ApiDrop) => Promise<void> | void) | undefined;
  readonly onExitFixedDropMode?: (() => void) | undefined;
  readonly wave: ApiWave;
  readonly dropId: string | null;
  readonly fixedDropMode: DropMode;
  readonly curationComposerVariant?: CurationComposerVariant | undefined;
  readonly initialCurationUrl?: string | null | undefined;
  readonly onSubmitCurationUrl?: ((url: string) => void) | undefined;
  readonly canSubmitCurationUrl?: boolean | undefined;
  readonly curationUrlSubmitRestrictionMessage?: string | null | undefined;
  readonly externalAttachmentDrop?: {
    readonly token: number;
    readonly files: File[];
  } | null | undefined;
  readonly onExternalAttachmentDropConsumed?: (() => void) | undefined;
  readonly termsSignatureFlowEnabled?: boolean | undefined;
  readonly identityPickerPlacement?: IdentityPickerPlacement | undefined;
  readonly forceStandardDropComposer?: boolean | undefined;
}
export default function PrivilegedDropCreator(t0) {
  const $ = _c(52);
  const {
    activeDrop,
    onCancelReplyQuote,
    wave,
    dropId,
    fixedDropMode,
    onAllDropsAdded,
    onServerDropCreated,
    onExitFixedDropMode,
    onDropAddedToQueue,
    curationComposerVariant: t1,
    initialCurationUrl: t2,
    onSubmitCurationUrl,
    canSubmitCurationUrl,
    curationUrlSubmitRestrictionMessage,
    externalAttachmentDrop,
    onExternalAttachmentDropConsumed,
    termsSignatureFlowEnabled: t3,
    identityPickerPlacement: t4,
    forceStandardDropComposer: t5
  } = t0;
  const curationComposerVariant = t1 === undefined ? "default" : t1;
  const initialCurationUrl = t2 === undefined ? null : t2;
  const termsSignatureFlowEnabled = t3 === undefined ? true : t3;
  const identityPickerPlacement = t4 === undefined ? "modal" : t4;
  const forceStandardDropComposer = t5 === undefined ? false : t5;
  const queryClient = useQueryClient();
  const {
    connectedProfile,
    activeProfileProxy
  } = useAuth();
  const {
    updateEligibility
  } = useWaveEligibility();
  let t6;
  if ($[0] !== queryClient || $[1] !== wave.id) {
    t6 = () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.WAVE, {
          wave_id: wave.id
        }]
      }).catch(_temp);
    };
    $[0] = queryClient;
    $[1] = wave.id;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const refreshWaveAfterSlowModeExpires = t6;
  const t7 = !!connectedProfile?.handle;
  const t8 = !!activeProfileProxy;
  const t9 = !wave.chat.enabled;
  const t10 = wave.chat.slow_mode_cooldown_ms ?? null;
  const t11 = wave.chat.next_drop_allowed ?? null;
  const t12 = wave.participation.period?.min ?? null;
  const t13 = wave.participation.period?.max ?? null;
  const t14 = wave.participation.no_of_applications_allowed_per_participant ?? null;
  let t15;
  if ($[3] !== refreshWaveAfterSlowModeExpires || $[4] !== t10 || $[5] !== t11 || $[6] !== t12 || $[7] !== t13 || $[8] !== t14 || $[9] !== t7 || $[10] !== t8 || $[11] !== t9 || $[12] !== wave.chat.authenticated_user_eligible || $[13] !== wave.metrics.your_participation_drops_count || $[14] !== wave.participation.authenticated_user_eligible) {
    t15 = {
      isLoggedIn: t7,
      isProxy: t8,
      canChat: wave.chat.authenticated_user_eligible,
      canDrop: wave.participation.authenticated_user_eligible,
      chatDisabled: t9,
      slowModeCooldownMs: t10,
      nextDropAllowed: t11,
      submissionStarts: t12,
      submissionEnds: t13,
      maxDropsCount: t14,
      identityDropsCount: wave.metrics.your_participation_drops_count,
      onSlowModeCooldownExpired: refreshWaveAfterSlowModeExpires
    };
    $[3] = refreshWaveAfterSlowModeExpires;
    $[4] = t10;
    $[5] = t11;
    $[6] = t12;
    $[7] = t13;
    $[8] = t14;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
    $[12] = wave.chat.authenticated_user_eligible;
    $[13] = wave.metrics.your_participation_drops_count;
    $[14] = wave.participation.authenticated_user_eligible;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  const {
    submissionRestriction,
    chatRestriction
  } = useDropPrivileges(t15);
  const blockingChatRestriction = chatRestriction === ChatRestriction.SLOW_MODE ? null : chatRestriction;
  let t16;
  let t17;
  if ($[16] !== chatRestriction || $[17] !== updateEligibility || $[18] !== wave.id) {
    t16 = () => {
      updateEligibility(wave.id, {
        authenticated_user_chat_restriction: chatRestriction
      });
    };
    t17 = [chatRestriction, updateEligibility, wave.id];
    $[16] = chatRestriction;
    $[17] = updateEligibility;
    $[18] = wave.id;
    $[19] = t16;
    $[20] = t17;
  } else {
    t16 = $[19];
    t17 = $[20];
  }
  useEffect(t16, t17);
  if (submissionRestriction !== null && blockingChatRestriction !== null) {
    let t18;
    if ($[21] !== blockingChatRestriction || $[22] !== submissionRestriction) {
      t18 = <DropPlaceholder type="both" chatRestriction={blockingChatRestriction} submissionRestriction={submissionRestriction} />;
      $[21] = blockingChatRestriction;
      $[22] = submissionRestriction;
      $[23] = t18;
    } else {
      t18 = $[23];
    }
    return t18;
  }
  if (fixedDropMode === DropMode.CHAT && blockingChatRestriction !== null) {
    let t18;
    if ($[24] !== blockingChatRestriction) {
      t18 = <DropPlaceholder type="chat" chatRestriction={blockingChatRestriction} />;
      $[24] = blockingChatRestriction;
      $[25] = t18;
    } else {
      t18 = $[25];
    }
    return t18;
  }
  if (fixedDropMode === DropMode.PARTICIPATION && submissionRestriction !== null) {
    let t18;
    if ($[26] !== submissionRestriction) {
      t18 = <DropPlaceholder type="submission" submissionRestriction={submissionRestriction} />;
      $[26] = submissionRestriction;
      $[27] = t18;
    } else {
      t18 = $[27];
    }
    return t18;
  }
  let t18;
  if ($[28] !== chatRestriction || $[29] !== submissionRestriction) {
    t18 = {
      submissionRestriction,
      chatRestriction
    };
    $[28] = chatRestriction;
    $[29] = submissionRestriction;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== activeDrop || $[32] !== canSubmitCurationUrl || $[33] !== curationComposerVariant || $[34] !== curationUrlSubmitRestrictionMessage || $[35] !== dropId || $[36] !== externalAttachmentDrop || $[37] !== fixedDropMode || $[38] !== forceStandardDropComposer || $[39] !== identityPickerPlacement || $[40] !== initialCurationUrl || $[41] !== onAllDropsAdded || $[42] !== onCancelReplyQuote || $[43] !== onDropAddedToQueue || $[44] !== onExitFixedDropMode || $[45] !== onExternalAttachmentDropConsumed || $[46] !== onServerDropCreated || $[47] !== onSubmitCurationUrl || $[48] !== t18 || $[49] !== termsSignatureFlowEnabled || $[50] !== wave) {
    t19 = <CreateDrop activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} onAllDropsAdded={onAllDropsAdded} onServerDropCreated={onServerDropCreated} onExitFixedDropMode={onExitFixedDropMode} wave={wave} dropId={dropId} fixedDropMode={fixedDropMode} privileges={t18} onDropAddedToQueue={onDropAddedToQueue} curationComposerVariant={curationComposerVariant} initialCurationUrl={initialCurationUrl} onSubmitCurationUrl={onSubmitCurationUrl} canSubmitCurationUrl={canSubmitCurationUrl} curationUrlSubmitRestrictionMessage={curationUrlSubmitRestrictionMessage} externalAttachmentDrop={externalAttachmentDrop} onExternalAttachmentDropConsumed={onExternalAttachmentDropConsumed} termsSignatureFlowEnabled={termsSignatureFlowEnabled} identityPickerPlacement={identityPickerPlacement} forceStandardDropComposer={forceStandardDropComposer} />;
    $[31] = activeDrop;
    $[32] = canSubmitCurationUrl;
    $[33] = curationComposerVariant;
    $[34] = curationUrlSubmitRestrictionMessage;
    $[35] = dropId;
    $[36] = externalAttachmentDrop;
    $[37] = fixedDropMode;
    $[38] = forceStandardDropComposer;
    $[39] = identityPickerPlacement;
    $[40] = initialCurationUrl;
    $[41] = onAllDropsAdded;
    $[42] = onCancelReplyQuote;
    $[43] = onDropAddedToQueue;
    $[44] = onExitFixedDropMode;
    $[45] = onExternalAttachmentDropConsumed;
    $[46] = onServerDropCreated;
    $[47] = onSubmitCurationUrl;
    $[48] = t18;
    $[49] = termsSignatureFlowEnabled;
    $[50] = wave;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  return t19;
}
function _temp() {}